import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  NotificationKeys,
  showLeaveRouteNotification,
} from '../../../store/actions/notifications';
import {
  clearRouteDetail,
  hideAllNotifications,
  resetAllTransferFromRouteEntity,
  setSaveRouteEnable,
  unlockRoute,
} from '../../../store/reducers/route';
import { onCloseScheduleRoute } from '../../../store/actions/schedule';
import useLocationQueryParams from '../../../hooks/useLocationQueryParams';
import { setStopMarkerPopup } from '../../../store/reducers/map';
import { hideNotifications } from '../../../store/reducers/notifications';
import usePrompt from '../../../hooks/usePrompt';
import usePrevious from '../../../hooks/usePrevious';
import { useModalContext } from '../../../components/ModalControlled/ModalProvider';
import { isDrawerModalOpen } from '../../../store/selectors/modal';

const RouteLeavingGuard = ({ shouldBlockNavigation }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { routeId, detailVisible } = useLocationQueryParams();

  const prevDetailVisible = usePrevious(detailVisible);
  const prevRouteId = usePrevious(routeId);

  const { closeAllModals } = useModalContext();

  const isModalOpen = useSelector(isDrawerModalOpen);

  const [confirmed, setConfirmed] = useState(false);
  const [retryFn, setRetryFn] = useState(null);

  const handleConfirm = useCallback(
    (retryNavigation, cancelNavigation) => {
      setRetryFn(() => retryNavigation);
      dispatch(
        showLeaveRouteNotification(
          () => setConfirmed(true),
          () => {
            cancelNavigation();
            setConfirmed(false);
            setRetryFn(null);
          }
        )
      );
    },
    [dispatch]
  );

  const nextLocation = usePrompt(true, shouldBlockNavigation, handleConfirm);

  const handleChangeRoute = useCallback(async () => {
    await dispatch(unlockRoute());
    dispatch(resetAllTransferFromRouteEntity());
    dispatch(hideAllNotifications());
    dispatch(setSaveRouteEnable(false));
    dispatch(onCloseScheduleRoute());
    dispatch(clearRouteDetail());
    dispatch(setStopMarkerPopup(null));
  }, [dispatch]);

  useEffect(() => {
    const routeChanged = prevRouteId && routeId !== prevRouteId;
    const detailClosed = prevDetailVisible && !detailVisible && isModalOpen;

    if (routeChanged) {
      (async () => {
        await closeAllModals();
        await handleChangeRoute();
      })();
    } else if (detailClosed) {
      closeAllModals();
    }
  }, [
    routeId,
    handleChangeRoute,
    prevRouteId,
    closeAllModals,
    detailVisible,
    isModalOpen,
    prevDetailVisible,
  ]);

  useEffect(() => {
    if (confirmed && retryFn && nextLocation) {
      retryFn();
      dispatch(hideNotifications([NotificationKeys.LEAVE_ROUTE_NOTIFICATION]));
      setConfirmed(false);
      setRetryFn(null);
      navigate(nextLocation);
    }
  }, [confirmed, retryFn, nextLocation, routeId, handleChangeRoute, dispatch]);

  return null;
};

export default RouteLeavingGuard;
